import * as React from 'react'
import { useSelector } from 'react-redux'
import { Box, Tab, Tabs, Grid, Paper, useTheme, useMediaQuery } from '@mui/material'
import { Navigate, useNavigate, useParams } from 'react-router-dom'

import ListAltIcon from '@mui/icons-material/ListAlt'
import FamilyTable from '../../components/Table/family'
import SpecieTable from '../../components/Table/specie'
import UserManagement from '../../components/Table/user/UserManagement'
import PseudonymTable from '../../components/Table/pseudonym'
import NewsTable from '../../components/Table/news'
import PublicEventsTable from '../../components/Table/publicEvents'
import TreeTable from '../../components/Table/tree'
import * as RT from '../../config/routeType'
import { ADMINISTRATION_DEFAULT_ROUTE, ADMINISTRATION_ROUTE } from '../../config/routes'

const ADMINISTRATION_TABS = [
  { slug: RT.ADMINISTRATION_TAB_FAMILIES, label: 'Familias' },
  { slug: RT.ADMINISTRATION_TAB_SPECIES, label: 'Especies' },
  { slug: RT.ADMINISTRATION_TAB_PSEUDONYMS, label: 'Pseudonimos' },
  { slug: RT.ADMINISTRATION_TAB_NEWS, label: 'Noticias' },
  { slug: RT.ADMINISTRATION_TAB_USERS, label: 'Usuarios' },
  { slug: RT.ADMINISTRATION_TAB_TESTS, label: 'Pruebas' },
  { slug: RT.ADMINISTRATION_TAB_TREES, label: 'Árboles' },
]

const ManagementTable = () => {
  const navigate = useNavigate()
  const { tab } = useParams()
  const _state = useSelector(state => state.drawerLeft)
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const value = ADMINISTRATION_TABS.findIndex(item => item.slug === tab)

  // Tabs ya visitados: se montan la primera vez y permanecen montados para no
  // perder paginación, filtros ni modales al cambiar de tab.
  const [visitedTabs, setVisitedTabs] = React.useState(() =>
    value >= 0 ? new Set([value]) : new Set(),
  )

  React.useEffect(() => {
    if (value < 0) return
    setVisitedTabs(prev => (prev.has(value) ? prev : new Set(prev).add(value)))
  }, [value])

  const handleChange = (event, newValue) => {
    const selectedTab = ADMINISTRATION_TABS[newValue]
    if (selectedTab) {
      navigate(`${ADMINISTRATION_ROUTE}/${selectedTab.slug}`, { replace: false })
    }
  }

  if (value === -1) {
    return <Navigate to={ADMINISTRATION_DEFAULT_ROUTE} replace />
  }

  // Color sólido Verde sage de tu paleta
  const backgroundColor = '#4E6A5B'

  // Estilos para desktop - Compatible con MUI v5
  const STYLE_TABS_DESKTOP = {
    orientation: 'vertical',
    variant: 'scrollable',
    'aria-label': 'vertical tabs',
    sx: {
      borderRight: 1,
      borderColor: 'divider',
      width: '160px',
      minHeight: '400px',
      '& .MuiTab-root': {
        minWidth: '160px',
        alignItems: 'flex-start',
        textAlign: 'left',
        justifyContent: 'flex-start',
      },
    },
  }

  // Estilos para móvil - Compatible con MUI v5
  const STYLE_TABS_MOBILE = {
    variant: 'scrollable',
    scrollButtons: 'auto',
    'aria-label': 'horizontal tabs',
    sx: {
      borderBottom: 1,
      borderColor: 'divider',
      width: '100%',
      '& .MuiTab-root': {
        minWidth: 'auto',
        fontSize: '0.75rem',
        padding: '6px 8px',
        minHeight: '48px',
      },
    },
  }

  const STYLE_TAB = {
    fontSize: isMobile ? '0.75rem' : '0.875rem',
    color: '#ffffff',
    fontWeight: 500,
    textTransform: 'none',
    minHeight: isMobile ? '48px' : '64px',
    '&:hover': {
      color: '#ffffff',
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
    },
    '&.Mui-selected': {
      color: '#ffffff',
      backgroundColor: 'rgba(255, 255, 255, 0.2)',
      fontWeight: 600,
    },
  }

  const STYLE_BOX = {
    flexGrow: 1,
    bgcolor: 'transparent',
    display: 'flex',
    flexDirection: isMobile ? 'column' : 'row',
    minHeight: isMobile ? '400px' : '500px',
    width: '100%',
  }

  const styleBoxContainerMap = {
    ml: !isMobile && _state?.open ? `${_state.drawerWidth}px` : 0,
    mt: _state?.drawerHeight || 0,
    width: '100%',
    height: _state?.contentBox || 'auto',
    display: 'flex',
    flexDirection: 'column',
    p: isMobile ? 1 : 2,
  }

  const paperStyles = {
    backgroundColor: backgroundColor,
    borderRadius: 5,
    overflow: 'hidden',
    width: '100%',
    flex: 1,
    minHeight: isMobile ? '400px' : '500px',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
  }

  const contentAreaStyles = {
    flexGrow: 1,
    backgroundColor: 'rgba(255, 255, 255, 0.98)',
    borderRadius: isMobile ? '0 0 8px 8px' : '0 8px 8px 0',
    minHeight: isMobile ? '350px' : '450px',
    overflow: 'hidden',
    position: 'relative',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    p: 2,
  }

  function a11yProps(index) {
    return {
      id: `simple-tab-${index}`,
      'aria-controls': `simple-tabpanel-${index}`,
    }
  }

  // Componente de panel personalizado para MUI v5.
  // `mounted` controla el montaje perezoso con keep-alive: una vez visitado el
  // tab, su contenido permanece montado (oculto con display:none) para conservar
  // paginación, filtros y modales al cambiar de tab.
  const TabPanel = ({ children, value, index, mounted, ...other }) => {
    return (
      <div
        role="tabpanel"
        hidden={value !== index}
        id={`simple-tabpanel-${index}`}
        aria-labelledby={`simple-tab-${index}`}
        style={{
          width: '100%',
          height: '100%',
          display: value === index ? 'flex' : 'none',
          flexDirection: 'column',
        }}
        {...other}>
        {mounted && (
          <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
            {children}
          </Box>
        )}
      </div>
    )
  }

  return (
    <Box sx={styleBoxContainerMap}>
      <Paper sx={paperStyles}>
        <Box sx={STYLE_BOX}>
          <Tabs
            value={value}
            onChange={handleChange}
            {...(isMobile ? STYLE_TABS_MOBILE : STYLE_TABS_DESKTOP)}>
            <Tab
              sx={STYLE_TAB}
              label={
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexDirection: isMobile ? 'column' : 'row',
                    gap: isMobile ? 0.5 : 1,
                  }}>
                  <ListAltIcon sx={{ fontSize: isMobile ? 16 : 20 }} />
                  <span>{ADMINISTRATION_TABS[0].label}</span>
                </Box>
              }
              {...a11yProps(0)}
            />
            <Tab
              sx={STYLE_TAB}
              label={
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexDirection: isMobile ? 'column' : 'row',
                    gap: isMobile ? 0.5 : 1,
                  }}>
                  <ListAltIcon sx={{ fontSize: isMobile ? 16 : 20 }} />
                  <span>{ADMINISTRATION_TABS[1].label}</span>
                </Box>
              }
              {...a11yProps(1)}
            />
            <Tab
              sx={STYLE_TAB}
              label={
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexDirection: isMobile ? 'column' : 'row',
                    gap: isMobile ? 0.5 : 1,
                  }}>
                  <ListAltIcon sx={{ fontSize: isMobile ? 16 : 20 }} />
                  <span>{ADMINISTRATION_TABS[2].label}</span>
                </Box>
              }
              {...a11yProps(2)}
            />
            <Tab
              sx={STYLE_TAB}
              label={
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexDirection: isMobile ? 'column' : 'row',
                    gap: isMobile ? 0.5 : 1,
                  }}>
                  <ListAltIcon sx={{ fontSize: isMobile ? 16 : 20 }} />
                  <span>{ADMINISTRATION_TABS[3].label}</span>
                </Box>
              }
              {...a11yProps(3)}
            />
            <Tab
              sx={STYLE_TAB}
              label={
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexDirection: isMobile ? 'column' : 'row',
                    gap: isMobile ? 0.5 : 1,
                  }}>
                  <ListAltIcon sx={{ fontSize: isMobile ? 16 : 20 }} />
                  <span>{ADMINISTRATION_TABS[4].label}</span>
                </Box>
              }
              {...a11yProps(4)}
            />
            <Tab
              sx={STYLE_TAB}
              label={
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexDirection: isMobile ? 'column' : 'row',
                    gap: isMobile ? 0.5 : 1,
                  }}>
                  <ListAltIcon sx={{ fontSize: isMobile ? 16 : 20 }} />
                  <span>{ADMINISTRATION_TABS[5].label}</span>
                </Box>
              }
              {...a11yProps(5)}
            />
            <Tab
              sx={STYLE_TAB}
              label={
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexDirection: isMobile ? 'column' : 'row',
                    gap: isMobile ? 0.5 : 1,
                  }}>
                  <ListAltIcon sx={{ fontSize: isMobile ? 16 : 20 }} />
                  <span>{ADMINISTRATION_TABS[6].label}</span>
                </Box>
              }
              {...a11yProps(6)}
            />
          </Tabs>

          {/* Área de contenido */}
          <Box sx={contentAreaStyles}>
            <TabPanel value={value} index={0} mounted={visitedTabs.has(0)}>
              <Grid container spacing={2} sx={{ height: '100%', width: '100%' }}>
                <Grid item xs={12} sx={{ height: '100%' }}>
                  <FamilyTable />
                </Grid>
              </Grid>
            </TabPanel>

            <TabPanel value={value} index={1} mounted={visitedTabs.has(1)}>
              <Grid container spacing={2} sx={{ height: '100%', width: '100%' }}>
                <Grid item xs={12} sx={{ height: '100%' }}>
                  <SpecieTable />
                </Grid>
              </Grid>
            </TabPanel>

            <TabPanel value={value} index={2} mounted={visitedTabs.has(2)}>
              <Grid container spacing={2} sx={{ height: '100%', width: '100%' }}>
                <Grid item xs={12} sx={{ height: '100%' }}>
                  <PseudonymTable />
                </Grid>
              </Grid>
            </TabPanel>

            <TabPanel value={value} index={3} mounted={visitedTabs.has(3)}>
              <Grid container spacing={2} sx={{ height: '100%', width: '100%' }}>
                <Grid item xs={12} sx={{ height: '100%' }}>
                  <NewsTable />
                </Grid>
              </Grid>
            </TabPanel>

            <TabPanel value={value} index={4} mounted={visitedTabs.has(4)}>
              <UserManagement />
            </TabPanel>

            <TabPanel value={value} index={5} mounted={visitedTabs.has(5)}>
              <Grid container spacing={2} sx={{ height: '100%', width: '100%' }}>
                <Grid item xs={12} sx={{ height: '100%' }}>
                  <PublicEventsTable />
                </Grid>
              </Grid>
            </TabPanel>

            <TabPanel value={value} index={6} mounted={visitedTabs.has(6)}>
              <Grid container spacing={2} sx={{ height: '100%', width: '100%' }}>
                <Grid item xs={12} sx={{ height: '100%' }}>
                  <TreeTable />
                </Grid>
              </Grid>
            </TabPanel>
          </Box>
        </Box>
      </Paper>
    </Box>
  )
}

export default ManagementTable
